// Story 032 — pic2comp: map an image pixel to a 3D point in the blade's
// component coordinate system, and derive the distance from the rotor
// rotation axis (the "distance from root" / radius readout, SB6-ANA-VIEW-03).
//
// Ported verbatim from the reference Python in
//   _BACKEND/.knowledge/ELEKS Platform - Extended Specification Document
//     - pic2comp Transformation.docx
// (§3.1 Formulars / §3.2 Python Implementation). It is a *simplified*
// transform — it does NOT model perspective distortion, matching the spec.
//
// Coordinate systems (spec §2):
//   PIC-CS  : pixels, origin top-left, u→right, v→down.
//   COMP-CS : metres, blade-attached. z = rotor-rotation-axis → tip (along the
//             blade), x = out of the leading edge, y = right-hand rule. The
//             rotor rotation axis lies along x, so the distance of a surface
//             point from that axis is hypot(comp_y, comp_z) (spec §"Distance
//             from Rotation Axis").
//
// The transform is scale-invariant in the ratios u/Wpx and v/Hpx, so callers
// that only have a *normalised* cursor (fraction of image width/height) can
// pass it as (u=nx, v=ny, Wpx=1, Hpx=1) — see `radiusFromNormalizedCursor`.

/** Camera intrinsics + pose, all required and finite. Angles in degrees. */
export interface Pic2CompParams {
  /** sensor width, mm (global_cameras.sensor_width_mm). */
  sensorWidthMm: number;
  /** sensor height, mm (global_cameras.sensor_height_mm). */
  sensorHeightMm: number;
  /** lens focal length, mm (images.focal_length_mm). Must be non-zero. */
  focalLengthMm: number;
  /** camera→surface distance, m (data_points.normal_obj_dist). */
  normalObjDist: number;
  /** camera position in COMP-CS, m (data_points.component_x). */
  camPosComponentX: number;
  /** camera position in COMP-CS, m (data_points.component_y). */
  camPosComponentY: number;
  /** camera position in COMP-CS, m (data_points.component_z). */
  camPosComponentZ: number;
  /** camera roll about the North axis, deg (data_points.component_roll). */
  camOriComponentRoll: number;
  /** camera pitch about the East axis, deg (data_points.component_pitch). */
  camOriComponentPitch: number;
  /** camera yaw about the Down axis, deg (data_points.component_yaw). */
  camOriComponentYaw: number;
}

export interface Pic2CompResult {
  /** out-of-LE component, m. */
  compX: number;
  /** off-plane component, m. */
  compY: number;
  /** along-blade (root→tip) component, m. */
  compZ: number;
  /**
   * Distance from the rotor rotation axis = hypot(compY, compZ), m. This is
   * the value shown in the Viewer's "distance from root" readout.
   */
  radiusM: number;
}

const DEG = Math.PI / 180;

/**
 * Transform a picture-space pixel `(u, v)` of an image `imageWidthPx ×
 * imageHeightPx` into the component coordinate system, using the camera
 * intrinsics + pose in `p`.
 *
 * Mirrors the spec's `pic2comp_complete`:
 *   y_s = (u - W/2) · sw / W              (sensor East, mm)
 *   z_s = (v - H/2) · sh / H              (sensor Down, mm)
 *   p_cam = d · [1, y_s/f, z_s/f]         (camera-space NED ray · distance)
 *   R = Rz(yaw) · Ry(pitch) · Rx(roll)    (aerospace NED rotation)
 *   p_comp = R · p_cam + [camX, camY, camZ]
 */
export function pic2comp(
  u: number,
  v: number,
  imageWidthPx: number,
  imageHeightPx: number,
  p: Pic2CompParams,
): Pic2CompResult {
  // pixel → sensor (mm)
  const yS = ((u - imageWidthPx / 2) * p.sensorWidthMm) / imageWidthPx; // East
  const zS = ((v - imageHeightPx / 2) * p.sensorHeightMm) / imageHeightPx; // Down

  // camera-space point (NED), distance d along the ray
  const camX = p.normalObjDist * 1.0;
  const camY = p.normalObjDist * (yS / p.focalLengthMm);
  const camZ = p.normalObjDist * (zS / p.focalLengthMm);

  // rotation R = Rz(yaw) · Ry(pitch) · Rx(roll), applied to the camera vector.
  const r = p.camOriComponentRoll * DEG;
  const pitch = p.camOriComponentPitch * DEG;
  const y = p.camOriComponentYaw * DEG;
  const cr = Math.cos(r);
  const sr = Math.sin(r);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const cy = Math.cos(y);
  const sy = Math.sin(y);

  // R = Rz · Ry · Rx, expanded so we avoid allocating matrices on every
  // mouse move. Rows of the combined 3×3 rotation:
  const r00 = cy * cp;
  const r01 = cy * sp * sr - sy * cr;
  const r02 = cy * sp * cr + sy * sr;
  const r10 = sy * cp;
  const r11 = sy * sp * sr + cy * cr;
  const r12 = sy * sp * cr - cy * sr;
  const r20 = -sp;
  const r21 = cp * sr;
  const r22 = cp * cr;

  const compX = r00 * camX + r01 * camY + r02 * camZ + p.camPosComponentX;
  const compY = r10 * camX + r11 * camY + r12 * camZ + p.camPosComponentY;
  const compZ = r20 * camX + r21 * camY + r22 * camZ + p.camPosComponentZ;

  return {
    compX,
    compY,
    compZ,
    // Distance from the rotation axis (along x) = hypot(y, z) — spec §"Distance
    // from Rotation Axis".
    radiusM: Math.hypot(compY, compZ),
  };
}

/**
 * The subset of `ImageGeometry` (lyse/v1 ≥ 6.3.0) that the transform consumes.
 * Every field is nullable because a pre-6.3.0 server (or an image without a
 * matched camera / data point) omits them.
 */
export interface Pic2CompGeometryInput {
  componentX?: number | null;
  componentY?: number | null;
  componentZ?: number | null;
  normalObjDist?: number | null;
  componentRoll?: number | null;
  componentPitch?: number | null;
  componentYaw?: number | null;
  focalLengthMm?: number | null;
  sensorWidthMm?: number | null;
  sensorHeightMm?: number | null;
}

function finite(n: number | null | undefined): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/**
 * Resolve an `ImageGeometry` into a complete `Pic2CompParams`, or `null` when
 * any required field is missing / non-finite, or the focal length is zero
 * (which would make the transform diverge). Used by the Viewer readout to
 * decide between rendering a value and rendering `—`.
 */
export function toPic2CompParams(
  g: Pic2CompGeometryInput,
): Pic2CompParams | null {
  if (
    !finite(g.sensorWidthMm) ||
    !finite(g.sensorHeightMm) ||
    !finite(g.focalLengthMm) ||
    g.focalLengthMm === 0 ||
    !finite(g.normalObjDist) ||
    !finite(g.componentX) ||
    !finite(g.componentY) ||
    !finite(g.componentZ) ||
    !finite(g.componentRoll) ||
    !finite(g.componentPitch) ||
    !finite(g.componentYaw)
  ) {
    return null;
  }
  return {
    sensorWidthMm: g.sensorWidthMm,
    sensorHeightMm: g.sensorHeightMm,
    focalLengthMm: g.focalLengthMm,
    normalObjDist: g.normalObjDist,
    camPosComponentX: g.componentX,
    camPosComponentY: g.componentY,
    camPosComponentZ: g.componentZ,
    camOriComponentRoll: g.componentRoll,
    camOriComponentPitch: g.componentPitch,
    camOriComponentYaw: g.componentYaw,
  };
}

/**
 * Distance from the rotor rotation axis (m) at a *normalised* cursor position
 * `(nx, ny)` — each a fraction of the image's width / height in `[0, 1]`,
 * published by the Viewer's `onCursorImageNormalized` so the value is the same
 * whether the thumbnail or the full-resolution image is currently loaded.
 *
 * Returns `null` when the geometry lacks the camera parameters (e.g. a
 * pre-6.3.0 server) or the result is non-finite. The transform is scale-
 * invariant in u/W and v/H, so we pass `(nx, ny)` with unit image dimensions.
 */
export function radiusFromNormalizedCursor(
  nx: number,
  ny: number,
  g: Pic2CompGeometryInput,
): number | null {
  const params = toPic2CompParams(g);
  if (params === null) return null;
  if (!Number.isFinite(nx) || !Number.isFinite(ny)) return null;
  const { radiusM } = pic2comp(nx, ny, 1, 1, params);
  return Number.isFinite(radiusM) ? radiusM : null;
}
